import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp
} from '@angular/fire/firestore';
import {
  isValidSlug,
  looksLikeDocumentId,
  nextSlugCandidate
} from '../utils/slug.utils';

export type SlugType = 'place' | 'tournament';

/**
 * A document in the `slugs` collection. The document id *is* the slug, which is
 * how uniqueness is enforced: Firestore cannot constrain a field, but it can
 * refuse to create a document that already exists.
 */
export interface SlugRecord {
  type: SlugType;
  targetId: string;
  createdAt?: any;
}

export interface ResolvedSlugTarget {
  /** The Firestore document id of the place/tournament. */
  id: string;
  /** Present when the entity has a slug and the URL should be rewritten to it. */
  canonicalSlug?: string;
}

const COLLECTION_FOR_TYPE: Record<SlugType, string> = {
  place: 'places',
  tournament: 'tournaments'
};

/** Raised when a slug is already taken by a different entity. */
export class SlugTakenError extends Error {
  constructor(public readonly slug: string) {
    super(`Slug "${slug}" is already in use.`);
  }
}

@Injectable({ providedIn: 'root' })
export class SlugService {
  private firestore = inject(Firestore);

  /**
   * Turns a route parameter into the real document id. Accepts both the legacy
   * `rPf5NQMG5qMdVIfnhX7M` form and a slug, so links shared before slugs
   * existed keep working forever.
   */
  async resolve(type: SlugType, param: string): Promise<ResolvedSlugTarget | null> {
    if (!param) return null;

    if (looksLikeDocumentId(param)) {
      const snap = await getDoc(doc(this.firestore, `${COLLECTION_FOR_TYPE[type]}/${param}`));
      if (!snap.exists()) return null;
      const slug = snap.data()?.['slug'] as string | undefined;
      return { id: param, canonicalSlug: slug };
    }

    const record = await this.readSlug(param);
    if (!record || record.type !== type) return null;

    // A renamed entity keeps its old slug documents as aliases, so the URL is
    // rewritten only when the caller used one of them.
    const canonical = await this.readCanonicalSlug(type, record.targetId);
    return {
      id: record.targetId,
      canonicalSlug: canonical && canonical !== param ? canonical : undefined
    };
  }

  async readSlug(slug: string): Promise<SlugRecord | null> {
    const snap = await getDoc(doc(this.firestore, `slugs/${slug}`));
    return snap.exists() ? (snap.data() as SlugRecord) : null;
  }

  /**
   * A slug can be used when it is free, or when it already points at the same
   * entity (i.e. the admin re-saved a form without touching the field).
   */
  async isAvailable(slug: string, targetId?: string): Promise<boolean> {
    if (!isValidSlug(slug)) return false;
    const record = await this.readSlug(slug);
    return !record || record.targetId === targetId;
  }

  /** Appends `-2`, `-3`, ... until a free slug is found. */
  async findAvailableSlug(base: string, targetId?: string, maxAttempts = 25): Promise<string> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const candidate = nextSlugCandidate(base, attempt);
      if (await this.isAvailable(candidate, targetId)) return candidate;
    }
    throw new SlugTakenError(base);
  }

  /**
   * Reserves `slug` for the entity and writes it back onto the entity document
   * in a single transaction, so a slug is never recorded without its owner.
   * Previous slugs are intentionally left in place as permanent aliases.
   */
  async claim(slug: string, type: SlugType, targetId: string): Promise<void> {
    if (!isValidSlug(slug)) throw new Error(`Invalid slug: "${slug}"`);

    const slugRef = doc(this.firestore, `slugs/${slug}`);
    const entityRef = doc(this.firestore, `${COLLECTION_FOR_TYPE[type]}/${targetId}`);

    await runTransaction(this.firestore, async tx => {
      const existing = await tx.get(slugRef);
      if (existing.exists()) {
        const record = existing.data() as SlugRecord;
        if (record.targetId !== targetId || record.type !== type) {
          throw new SlugTakenError(slug);
        }
      } else {
        tx.set(slugRef, { type, targetId, createdAt: serverTimestamp() } as SlugRecord);
      }

      tx.update(entityRef, { slug });
    });
  }

  private async readCanonicalSlug(type: SlugType, targetId: string): Promise<string | undefined> {
    const snap = await getDoc(doc(this.firestore, `${COLLECTION_FOR_TYPE[type]}/${targetId}`));
    return snap.exists() ? (snap.data()?.['slug'] as string | undefined) : undefined;
  }
}
